import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
    Shield,
    Plug,
    Monitor,
    Menu,
    X,
    Cog,
    Building2,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import {
    BRANCH_FILTER_CHANGED_EVENT,
    isOrgOverview,
    isOrgAdminBranchSelectionEnabled,
} from "../../utils/branchSession";
import useApiMethods from "../../lib/useApiMethods";
import { CUSTOM_CONFIGURATION } from "../../constants/api";
import { persistCustomConfig } from "../../utils/customConfigSession";
import { SESSION_STORAGE_HYDRATED_EVENT } from "../../hooks/use-navigation";


const SettingsLayout = () => {
    const { fetch } = useApiMethods();
    const location = useLocation();

    const is_admin = sessionStorage.getItem('is_admin') === 'true';
    const is_super_admin = sessionStorage.getItem('is_super_admin') === 'true';
    const is_branch_admin = sessionStorage.getItem('is_branch_admin') === 'true';
    const overview = isOrgOverview();
    const orgAdminBranchSelection = isOrgAdminBranchSelectionEnabled();
    const [, setSettingsRevision] = useState(0);

    useEffect(() => {
        const refresh = () => setSettingsRevision((n) => n + 1);
        window.addEventListener(SESSION_STORAGE_HYDRATED_EVENT, refresh);
        window.addEventListener(BRANCH_FILTER_CHANGED_EVENT, refresh);
        return () => {
            window.removeEventListener(SESSION_STORAGE_HYDRATED_EVENT, refresh);
            window.removeEventListener(BRANCH_FILTER_CHANGED_EVENT, refresh);
        };
    }, []);

    // Re-read branch mode when navigating between settings pages.
    useEffect(() => {
        setSettingsRevision((n) => n + 1);
    }, [location.pathname]);

    // Company-level Branch feature toggle (CustomConfiguration.is_branch_enabled).
    // When false with 2+ branches, the Branch tab is hidden and backend writes
    // are blocked. During initial setup (< 2 branches) the tab stays visible.
    //
    // Seed from the cached customConfig, then re-fetch the LIVE config on mount
    // so toggling the flag reflects immediately (the cached value can be stale —
    // it's only refreshed on dashboard load).
    const seed = JSON.parse(sessionStorage.getItem('customConfig') || '{}');
    const [branchEnabled, setBranchEnabled] = useState(seed.is_branch_enabled !== false);

    useEffect(() => {
        fetch(CUSTOM_CONFIGURATION)
            .then((res) => {
                // GET returns { status, data: [ {...config} ] } (a .values() list).
                const cfg = res?.status === "success" ? res?.data?.[0] : null;
                if (cfg && typeof cfg === 'object') {
                    setBranchEnabled(cfg.is_branch_enabled !== false);
                    persistCustomConfig(cfg);
                }
            })
            .catch(() => { /* keep the seeded value on error */ });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const branch_enabled = branchEnabled;
    const multi_branch = sessionStorage.getItem("multi_branch") === "true";

    // Base eligibility for the Branch tab: ORG ADMIN only (never a branch admin).
    // Show the tab when branch mgmt is enabled OR the company still has fewer than
    // two branches (initial setup). Hides the tab only when an org deliberately
    // disabled branch mgmt with 2+ branches already in place.
    const branch_tab_eligible =
        is_super_admin &&
        !is_branch_admin &&
        (branch_enabled || !multi_branch);

    // In Dashboard Overview (2+ branches), Branch management lives ONLY in the
    // overview's curated Settings.
    const show_branch_in_overview = branch_tab_eligible;

    // Org admin with branch management enabled always sees the Branch tab.
    // Previously hidden when branch_avail was true and a specific branch was
    // selected; that made branch setup unreachable after login.
    const show_branch_in_settings = branch_tab_eligible;

    // A manager (org admin OR branch admin) gets the full settings tabs:
    // Organization, Integrations, Configuration. The Organization page renders
    // read-only (a card) for a branch admin and editable for the org admin
    // (OrganizationEdit decides based on role).
    const is_manager = is_admin || is_super_admin || is_branch_admin;

    // Org admins see Configuration in both overview and direct branch view.
    // Branch managers use the full-app settings tabs (is_manager below).
    const showConfigurationInOverview = orgAdminBranchSelection;
    const showConfigurationInFullApp = is_manager;

    const tabs = overview
        ? [
            { name: "Appearance", icon: Monitor, route: "/settings/appearance", },
            { name: "Organization", icon: Building2, route: "/settings/organization-edit", },
            ...(show_branch_in_overview ? [{ name: "Branch", icon: Building2, route: "/settings/branch", }] : []),
            ...(showConfigurationInOverview ? [{ name: "Configuration", icon: Cog, route: "/settings/configuration", }] : []),
            { name: "Security & Access", icon: Shield, route: "/settings/security", },
        ]
        : [
            { name: "Appearance", icon: Monitor, route: "/settings/appearance", },
            ...(is_manager ? [
                { name: "Organization", icon: Building2, route: "/settings/organization-edit", },
                ...(show_branch_in_settings ? [{ name: "Branch", icon: Building2, route: "/settings/branch", }] : []),
                { name: "Integrations", icon: Plug, route: "/settings/integrations", },
                ...(showConfigurationInFullApp ? [{ name: "Configuration", icon: Cog, route: "/settings/configuration", }] : []),
            ] : []),
            { name: "Security & Access", icon: Shield, route: "/settings/security", },
        ];

    const visibleTabs = tabs;
    const [sidebarOpen, setSidebarOpen] = useState(false);

    return (
        <div className="flex flex-1 min-h-0 gap-3">
            {/* Mobile Hamburger (only show when sidebar is closed) */}
            {!sidebarOpen && (
                <Button
                    className="fixed top-2 left-16 md:hidden z-40 p-2 h-10 w-10 rounded-lg transition-all ease-in-out delay-300"
                    onClick={() => setSidebarOpen(true)}
                >
                    <Menu size={20} />
                </Button>
            )}


            {/* Sidebar */}
            <aside
                className={`fixed z-40 md:z-0 md:static top-0 left-0 h-full w-64 rounded-md bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 p-4 transform transition-transform duration-300 
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 overflow-y-auto`}
            >
                {/* Close button (mobile only) */}
                <div className="flex justify-between items-center mb-4 md:hidden">
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                        Settings
                    </h2>
                    <button onClick={() => setSidebarOpen(false)}>
                        <X size={20} className="text-gray-600 dark:text-gray-300" />
                    </button>
                </div>

                <h2 className="hidden md:block text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">
                    Settings
                </h2>

                <nav className="flex flex-col gap-2">
                    {visibleTabs.map((tab) => (
                        <NavLink
                            key={tab.route}
                            to={tab.route}
                            onClick={() => setSidebarOpen(false)} // auto close on mobile
                            className={({ isActive }) =>
                                `flex items-center gap-2 px-3 py-2 rounded-md transition-colors ${isActive
                                    ? "bg-sidebar/10 text-sidebar-primary font-medium dark:bg-sidebar-primary/20 dark:text-sidebar-primary"
                                    : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                                }`
                            }
                        >
                            <tab.icon size={18} />
                            {tab.name}
                        </NavLink>
                    ))}
                </nav>
            </aside>

            {/* Main content */}
            <main className="flex-1 min-h-0 p-6 overflow-y-auto text-gray-900 dark:text-gray-100 dark:bg-gray-900 bg-gray-50 rounded-md">
                <Outlet />
            </main>
        </div>
    );
};

export default SettingsLayout;